import { useCallback } from 'react';
import { FormMode } from '@sib/utils';
import { DetailsModalView } from '@sidecar/common/components/DetailsModal';
import { useTicketRouteNavigation } from '../use-ticket-route-navigation';
import {
  isTicketCreateRoute,
  isTicketEditRoute,
  TICKET_ROUTE_KEY,
} from '../ticket-route.utils';

interface UseTicketModalActionsProps {
  navigateToRoute: ReturnType<
    typeof useTicketRouteNavigation
  >['navigateToRoute'];
  navigateToReturnOrRoot: ReturnType<
    typeof useTicketRouteNavigation
  >['navigateToReturnOrRoot'];
  pathname: string;
  ticketId?: string;
}

const useTicketModalActions = ({
  navigateToRoute,
  navigateToReturnOrRoot,
  pathname,
  ticketId,
}: UseTicketModalActionsProps) => {
  const openTicket = useCallback(
    (id: string) =>
      navigateToRoute(TICKET_ROUTE_KEY.details, {
        ticketId: id,
        returnTo: pathname,
      }),
    [navigateToRoute, pathname]
  );

  const openCreate = useCallback(
    () => navigateToRoute(TICKET_ROUTE_KEY.new),
    [navigateToRoute]
  );

  const close = useCallback(
    () => navigateToReturnOrRoot(),
    [navigateToReturnOrRoot]
  );

  const setView = useCallback(
    (nextView: DetailsModalView) => {
      if (!ticketId) return;

      navigateToRoute(
        nextView === DetailsModalView.Form
          ? TICKET_ROUTE_KEY.edit
          : TICKET_ROUTE_KEY.details,
        { ticketId }
      );
    },
    [navigateToRoute, ticketId]
  );

  return { openTicket, openCreate, close, setView };
};

export const useTicketDetailsModalActions = () => {
  const { navigateToRoute, navigateToReturnOrRoot, pathname } =
    useTicketRouteNavigation();

  return useTicketModalActions({
    navigateToRoute,
    navigateToReturnOrRoot,
    pathname,
  });
};

export const useTicketDetailsModalRouteParams = () => {
  const {
    params,
    pathname,
    returnTo,
    navigateToRoute,
    navigateToReturnOrRoot,
  } = useTicketRouteNavigation();
  const { ticketId } = params;
  const { openTicket, openCreate, close, setView } = useTicketModalActions({
    navigateToRoute,
    navigateToReturnOrRoot,
    pathname,
    ticketId,
  });

  const isCreate = isTicketCreateRoute(pathname);
  const isOpen = Boolean(ticketId) || isCreate;
  const type = ticketId ? FormMode.Edit : FormMode.Create;
  const view =
    isCreate || isTicketEditRoute(pathname)
      ? DetailsModalView.Form
      : DetailsModalView.Details;

  return {
    isOpen,
    ticketId,
    type,
    view,
    returnTo,
    openTicket,
    openCreate,
    setView,
    close,
  };
};
